/**
 * @file `LowerSplit` — the detail pattern for every table panel: a
 *       table region on top + a resizable lower detail pane below.
 * @module @labcraft/admin/ui/kernel/lower-split
 *
 * Replaces the per-panel slide-in detail drawer. Clicking a row opens
 * its detail in the lower pane (not a drawer); the divider is dragged
 * with a `row-resize` cursor and the chosen height is persisted per
 * `storageKey` in localStorage so each panel remembers its own split.
 * Mirrors the Social Needs-you review split from the design SoT.
 *
 * The component is presentational — it owns only the pane height and
 * the drag interaction. Panels supply the table (`children`) and the
 * detail (`detail`); `open` toggles the lower pane.
 */

import { useCallback, useRef, useState, type JSX, type ReactNode } from 'react'

/** Minimum lower-pane height in px; below this the detail is unreadable. */
const MIN_PANE_H = 150

/** Reserve for the table region above the divider when computing max. */
const TOP_RESERVE = 130

/**
 * Fallback lower-pane height when localStorage has no persisted value.
 * Viewport-relative (~45% of the window) so the detail opens usefully tall
 * on first click instead of a thin fixed strip that always needs a drag;
 * falls back to a taller fixed height when there is no `window` (tests/SSR).
 */
function defaultPaneHeight(): number {
  if (typeof window === 'undefined') return 460
  return Math.round(window.innerHeight * 0.45)
}

/** Props for {@link LowerSplit}. */
export type LowerSplitProps = {
  /** Whether the lower detail pane is shown (a row is selected). */
  open: boolean
  /** localStorage key the chosen pane height persists under. */
  storageKey: string
  /** Detail content rendered in the lower pane when `open`. */
  detail: ReactNode
  /** The table region rendered in the upper pane. */
  children: ReactNode
}

/**
 * Read the persisted pane height for a panel, falling back to the
 * default when absent or unparseable.
 *
 * @param storageKey - The localStorage key to read.
 * @returns The persisted height in px, or {@link defaultPaneHeight}.
 */
function readPaneHeight(storageKey: string): number {
  if (typeof localStorage === 'undefined') return defaultPaneHeight()
  const v = Number.parseInt(localStorage.getItem(storageKey) ?? '', 10)
  return Number.isFinite(v) ? v : defaultPaneHeight()
}

/**
 * Clamp a candidate pane height into the legal range for a container.
 *
 * @param candidate - The proposed height in px.
 * @param boxHeight - The split container's height in px (0 if unknown).
 * @returns The height clamped to `[MIN_PANE_H, boxHeight - TOP_RESERVE]`.
 */
function clampPaneHeight(candidate: number, boxHeight: number): number {
  const max = boxHeight > 0 ? boxHeight - TOP_RESERVE : 600
  return Math.max(MIN_PANE_H, Math.min(max, candidate))
}

/** Resizable table-over-detail split; the detail replaces the drawer. */
export function LowerSplit({
  open,
  storageKey,
  detail,
  children,
}: LowerSplitProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number>(() => readPaneHeight(storageKey))

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const startY = e.clientY
      const startH = height
      const box = ref.current?.getBoundingClientRect()
      let last = startH
      const onMove = (ev: MouseEvent): void => {
        last = clampPaneHeight(startH - (ev.clientY - startY), box?.height ?? 0)
        setHeight(last)
      }
      const onUp = (): void => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        try {
          localStorage.setItem(storageKey, String(Math.round(last)))
        } catch {
          /* localStorage may be unavailable; height stays in-memory */
        }
      }
      document.body.style.cursor = 'row-resize'
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [height, storageKey],
  )

  return (
    <div
      ref={ref}
      className="ny"
      style={{
        gridTemplateRows: open ? `minmax(120px, 1fr) 8px ${height}px` : '1fr',
      }}>
      {children}
      {open && (
        <div
          className="ny-divider"
          onMouseDown={startResize}
          role="separator"
          aria-orientation="horizontal"
          title="Drag to resize"
        />
      )}
      {open && <div className="ny-review">{detail}</div>}
    </div>
  )
}
